import { Exercise } from "@/Components/Exercises/exerciseSchema";
import Checkbox from "@/Components/UI/Forms/Checkbox";
import MultiSelectDropdown, {
    MultiSelectOption,
} from "@/Components/UI/Forms/MultiSelectDropdown";
import { ExercisesSeenContext } from "@/Contexts/ExercisesSeenContext";
import { ClientExercise } from "@/types/exercise";
import { useDebounce } from "@/utils/useDebounce";
import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import TextInput from "../UI/Forms/TextInput";
import { ExerciseCard } from "./ExerciseCard";

export type ExerciseButton = {
    id: string;
    tooltipTitle: string;
    buttonIcon: React.ReactNode;
    onClick: (exercise: Exercise) => void;
};

type ExerciseLibraryProps = {
    clientId?: number; // If provided, will show the most relevant exercise for the client
    exercises: Exercise[];
    latestUniqueClientExercises?: ClientExercise[];
    buttons?: (ExerciseButton | ((exercise: Exercise) => ExerciseButton))[];
};

export default function ExerciseLibrary({
    exercises,
    latestUniqueClientExercises,
    buttons,
    clientId,
}: ExerciseLibraryProps) {
    const [search, setSearch] = useState("");
    const [recommendedExercises, setRecommendedExercises] = useState<
        { exercise: Exercise; explanation: string | null }[]
    >([]);
    const { unseenExercises } = useContext(ExercisesSeenContext);
    const [isLoadingRecommended, setIsLoadingRecommended] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState<
        MultiSelectOption[]
    >([]);
    const [onlyShowNew, setOnlyShowNew] = useState(false);
    // Relevance-ordered exercise ids returned by the search endpoint, or null
    // when the search box is empty (and we fall back to the browse ordering).
    const [matchedIds, setMatchedIds] = useState<number[] | null>(null);

    const lastSentDates =
        latestUniqueClientExercises?.reduce(
            (acc, clientExercise) => {
                acc[clientExercise.exercise_id] = clientExercise.created_at;
                return acc;
            },
            {} as Record<number, string>,
        ) ?? {};

    const newExercisesCount = exercises.filter(
        (exercise) => !exercise.is_live && !!unseenExercises[exercise.id],
    ).length;

    const categories = exercises.reduce(
        (acc, exercise) => {
            exercise.categories?.forEach((category) => {
                acc[category.key] = {
                    id: category.key,
                    name: category.pretty_name,
                };
            });
            return acc;
        },
        {} as Record<string, MultiSelectOption>,
    );

    const requestRecommendedExercises = async () => {
        if (!clientId) return;
        try {
            setIsLoadingRecommended(true);
            const { data } = await axios.get<
                {
                    exercise_id: number;
                    explanation: string | null;
                    order: number;
                }[]
            >(route("exercises.recommended", clientId));
            const recommendedExercises: {
                exercise: Exercise;
                explanation: string | null;
            }[] = [];
            for (const item of data) {
                const exercise = exercises.find(
                    (e) => e.id === item.exercise_id,
                );
                if (exercise) {
                    recommendedExercises.push({
                        exercise,
                        explanation: item.explanation,
                    });
                }
            }
            setRecommendedExercises(recommendedExercises);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingRecommended(false);
        }
    };

    useEffect(() => {
        requestRecommendedExercises();
    }, [clientId]);

    // Once the coach has reviewed every new exercise (e.g. by previewing them
    // while this filter is active), clear the filter so the full library returns
    // instead of leaving them on an empty grid.
    useEffect(() => {
        if (newExercisesCount === 0 && onlyShowNew) {
            setOnlyShowNew(false);
        }
    }, [newExercisesCount, onlyShowNew]);

    // Bumped on every change so a slow in-flight response from an earlier query
    // can't overwrite the results of a later one.
    const latestSearchRef = useRef(0);

    const debouncedSearch = useDebounce(
        250,
        async (trimmedSearch: string, requestId: number) => {
            const { data } = await axios.get<number[]>(
                route("exercises.search", { search: trimmedSearch }),
            );
            if (requestId === latestSearchRef.current) {
                setMatchedIds(data);
            }
        },
    );

    const onSearchChanged = (value: string) => {
        setSearch(value);

        const requestId = ++latestSearchRef.current;
        const trimmedSearch = value.trim();
        if (trimmedSearch.length === 0) {
            setMatchedIds(null);
            return;
        }

        debouncedSearch(trimmedSearch, requestId);
    };

    const isSearching = matchedIds !== null;

    const augmentedExercises = exercises.map((exercise) => {
        const recommendation = recommendedExercises.find(
            (rec) => rec.exercise.id === exercise.id,
        );
        return {
            ...exercise,
            recommended: !!recommendation,
            recommendationExplanation: recommendation?.explanation ?? null,
        };
    });

    const categoryFilteredExercises = augmentedExercises
        .filter(
            (exercise) =>
                selectedCategories.length === 0 ||
                selectedCategories.some((selectedCategory) =>
                    exercise.categories?.some(
                        (category) => category.key === selectedCategory.id,
                    ),
                ),
        )
        .filter((exercise) => !onlyShowNew || !!unseenExercises[exercise.id])
        .filter((exercise) => !exercise.is_live);

    let visibleExercises;
    if (isSearching) {
        const relevanceOrder = new Map(
            (matchedIds ?? []).map((id, index) => [id, index]),
        );
        visibleExercises = categoryFilteredExercises
            .filter((exercise) => relevanceOrder.has(exercise.id))
            .sort(
                (a, b) =>
                    (relevanceOrder.get(a.id) ?? 0) -
                    (relevanceOrder.get(b.id) ?? 0),
            );
    } else {
        visibleExercises = categoryFilteredExercises.sort((a, b) => {
            if (a.recommended && !b.recommended) {
                return -1;
            } else if (!a.recommended && b.recommended) {
                return 1;
            } else if (a.is_bookmarked && !b.is_bookmarked) {
                return -1;
            } else if (b.is_bookmarked && !a.is_bookmarked) {
                return 1;
            }
            return a.name.localeCompare(b.name);
        });
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <TextInput
                    placeholder="Search..."
                    className="w-full lg:w-96"
                    value={search}
                    onChange={(e) => onSearchChanged(e.target.value)}
                />
                {Object.values(categories).length > 0 && (
                    <MultiSelectDropdown
                        placeholder="Filter by category"
                        containerClassName="w-96"
                        options={Object.values(categories)}
                        selectedOptions={selectedCategories}
                        onChange={setSelectedCategories}
                        pillClassNames="!bg-brand-blueDecal/50 !text-brand-gray uppercase !text-xs !font-medium !px-2"
                    />
                )}
                <Checkbox
                    name="onlyShowNewExercises"
                    label={`Only show new exercises (${newExercisesCount})`}
                    checked={onlyShowNew}
                    disabled={!newExercisesCount}
                    onChange={setOnlyShowNew}
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(425px,1fr))] gap-4">
                {isLoadingRecommended &&
                    [...Array(2)].map((_, index) => (
                        <div
                            key={index}
                            className="relative flex flex-col rounded-lg border border-brand-border p-4 bg-brand-lightGray overflow-hidden"
                        >
                            <div className="mx-auto mb-4 w-full aspect-square rounded-lg bg-brand-border animate-pulse" />
                            <div className="flex-1 flex flex-col gap-2">
                                <div className="h-6 w-3/4 bg-brand-border animate-pulse rounded" />
                                <div className="h-4 w-1/2 bg-brand-border animate-pulse rounded" />
                            </div>
                            <div className="absolute top-0 left-0 bg-brand-gray text-brand-white px-2 py-1 text-sm">
                                Recommended
                            </div>
                        </div>
                    ))}
                {visibleExercises.map((exercise) => (
                    <ExerciseCard
                        key={`${exercise.id}-${exercise.recommended ? "recommended" : ""}`}
                        exercise={exercise}
                        unseenState={unseenExercises[exercise.id]}
                        lastSentDate={lastSentDates[exercise.id]}
                        buttons={buttons}
                    />
                ))}
            </div>
        </div>
    );
}
